import { queryOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { client } from "./client"
import { safeQueryOptions } from "@src/utils/query"
import { components } from "./schema"
import { UnpackPromise } from "@src/utils/types"

export const surveyKeys = {
  all: ["surveys"],
  surveyDefinitions: (communityId?: number, take?: number, skip?: number) => [
    ...surveyKeys.all,
    "list",
    communityId,
    take,
    skip,
  ],
  surveyDefinition: (id?: number) => [...surveyKeys.all, id, "definition"],
  surveyResponse: (id?: number) => [...surveyKeys.all, "response", id],
  surveyResponses: (id?: number) => [...surveyKeys.all, "responses", id],
  latestUserSurveyResponse: (id?: number, communityId?: number) => [
    ...surveyKeys.all,
    id,
    communityId,
    "latest",
  ],
  latestProjectSurveyResponse: (
    id?: number,
    projectId?: number,
    communityId?: number,
  ) => [...surveyKeys.all, id, projectId, communityId, "latest"],
  surveyResponseFilters: (id: number) => [...surveyKeys.all, id, "filters"],
  responsesList: (id: number, filters: SurveyResponsesFilters) => [
    ...surveyKeys.all,
    id,
    "response",
    "list",
    filters,
  ],
  latestSurveyResponse: (id?: number) => [...surveyKeys.all, id, "latest"],
  projectSurveyResponses: (projectId?: number, communityId?: number) => [
    ...surveyKeys.all,
    projectId,
    communityId,
    "responses",
  ],
  userSurveyResponses: (userId?: number, communityId?: number) => [
    ...surveyKeys.all,
    userId,
    communityId,
    "responses",
  ],
  projectSurveyDefinitions: (projectId?: number, communityId?: number) => [
    ...surveyKeys.all,
    "project",
    "definitions",
    projectId,
    communityId,
  ],
}

export enum SurveyType {
  Individual = 0,
  Startup = 1,
}

export enum IterationType {
  Weekly = 0,
  Monthly = 1,
  Quarterly = 2,
  Semestral = 3,
  Yearly = 4,
  Custom = 5,
  NonRecurrent = 6,
}

export type SurveyDefinition = components["schemas"]["SurveyDefinitionDto"]
export type SurveyDefinitionUpdate =
  components["schemas"]["SurveyDefinitionUpdateDto"]

// The blob file isn't directly sent to the backend, but it's needed for the form to handle file
// uploads correctly. The form needs to upload the files first, get the URLs, and then send the
// URLs in the answers.
export type SurveyResponseCreation = Omit<
  components["schemas"]["SurveyResponseCreationDto"],
  "answers"
> & {
  answers: Array<
    components["schemas"]["SurveyFormAnswerBaseDto"] & {
      blob?: File | null
    }
  >
}
export type SurveyResponseCreationUpdateBase = Omit<
  components["schemas"]["SurveyResponseCreationUpdateBaseDto"],
  "answers"
> & {
  answers: Array<
    components["schemas"]["SurveyFormAnswerBaseDto"] & {
      blob?: File | null
    }
  >
}
export type SurveyResponse = components["schemas"]["SurveyResponseDto"]

export enum SurveyStatus {
  Draft = 0,
  Submitted = 1,
}

export const getSurveyDefinition = async (id: number) => {
  const data = await client.GET("/api/Survey/survey-definition/{id}", {
    params: {
      path: { id },
    },
  })
  return data
}

export const getSurveyDefinitionQuery = (id?: number) => ({
  ...safeQueryOptions({
    queryFn: getSurveyDefinition,
    queryKeys: surveyKeys.surveyDefinition(id),
    unsafeQueryFnArgs: [id],
  }),
  // A 4xx here is a verdict (no permission / no such survey), not a transient failure.
  // Retrying it only keeps the page in its loading state for several more seconds
  // before the error is finally shown.
  retry: (failureCount: number, error: Error) => {
    const status = (error as AxiosError).status

    if (status && status >= 400 && status < 500) return false

    return failureCount < 3
  },
})

export const saveSurveyDefinition = async (
  payload: SurveyDefinitionUpdate,
  surveyId?: number,
) => {
  if (!surveyId) {
    const data = await client.POST("/api/Survey/survey-definition", {
      body: payload,
    })

    return data
  }

  const data = await client.PUT("/api/Survey/survey-definitions/{surveyId}", {
    params: {
      path: { surveyId },
    },
    body: payload,
  })

  return data
}

export const getSurveyDefinitionList = async (
  communityId: number,
  skip: number,
  take: number,
) => {
  const data = await client.POST("/api/Survey/survey-definitions", {
    body: {
      communityId,
      skip,
      take,
    },
  })

  return data
}

export const getSurveyDefinitionListQuery = ({
  communityId,
  take,
  skip,
}: {
  communityId?: number
  take: number
  skip: number
}) =>
  safeQueryOptions({
    queryFn: getSurveyDefinitionList,
    queryKeys: surveyKeys.surveyDefinitions(communityId, take, skip),
    unsafeQueryFnArgs: [communityId, skip, take],
  })

export type SurveyResponsesFilters =
  components["schemas"]["SurveyResponsesFilterDto"]

export const getSurveyResponseList = async (
  id: number,
  filters: SurveyResponsesFilters,
) => {
  const data = await client.POST(
    "/api/Survey/survey-definition/{id}/survey-responses",
    {
      params: {
        path: { id },
      },
      body: filters,
    },
  )

  return data
}

export const getSurveyResponseListQuery = (
  id: number,
  filters: SurveyResponsesFilters,
  enabled = true,
) =>
  queryOptions({
    queryFn: () => getSurveyResponseList(id, filters),
    queryKey: surveyKeys.responsesList(id, filters),
    gcTime: 1000 * 60 * 24 * 7,
    enabled,
  })

export const getSurveyResponse = async (id: number) => {
  const data = await client.GET("/api/Survey/survey-response/{id}", {
    params: {
      path: { id },
    },
  })
  return data
}

export const getSurveyResponseQuery = (id?: number) =>
  safeQueryOptions({
    queryFn: getSurveyResponse,
    queryKeys: surveyKeys.surveyResponse(id),
    unsafeQueryFnArgs: [id],
  })

export const getLatestUserSurveyResponse = async (
  id: number,
  communityId: number,
) => {
  const data = await client.GET(
    "/api/Survey/{surveyId}/latest-user-survey-response",
    {
      params: {
        path: { surveyId: id },
        query: { communityId },
      },
    },
  )

  return data
}

export const getLatestUserSurveyResponseQuery = (
  id?: number,
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: getLatestUserSurveyResponse,
    queryKeys: surveyKeys.latestUserSurveyResponse(id, communityId),
    unsafeQueryFnArgs: [id, communityId],
  })

export const getLatestProjectSurveyResponse = async (
  surveyId: number,
  projectId: number,
  communityId: number,
) => {
  const data = await client.GET(
    "/api/Survey/{surveyId}/project/{projectId}/latest-project-survey-response",
    {
      params: {
        path: { surveyId, projectId },
        query: { communityId },
      },
    },
  )

  return data
}

export const getLatestProjectSurveyResponseQuery = (
  surveyId?: number,
  projectId?: number,
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: getLatestProjectSurveyResponse,
    queryKeys: surveyKeys.latestProjectSurveyResponse(
      surveyId,
      projectId,
      communityId,
    ),
    unsafeQueryFnArgs: [surveyId, projectId, communityId],
  })

export const deleteSurveyDefinition = async (surveyId: number) => {
  const data = await client.DELETE("/api/Survey/survey-definition/{surveyId}", {
    params: {
      path: { surveyId },
    },
  })

  return data
}

export const saveSurvey = async (
  payload: SurveyResponseCreation | SurveyResponseCreationUpdateBase,
  id?: number,
) => {
  if (id) {
    const data = await client.PATCH(
      `/api/Survey/survey-response/{surveyResponseId}`,
      {
        params: {
          path: { surveyResponseId: id },
        },
        body: payload,
      },
    )

    return data
  } else {
    const data = await client.POST("/api/Survey/survey-response", {
      body: payload,
    })

    return data
  }
}

export const getSurveyResponsesFilters = async (surveyId: number) => {
  const data = await client.GET(
    "/api/Survey/survey-definition/{id}/survey-response-filters",
    {
      params: {
        path: { id: surveyId },
      },
    },
  )

  return data
}

export type SurveyFilters = UnpackPromise<
  ReturnType<typeof getSurveyResponsesFilters>
>

export const getSurveyResponsesFiltersQuery = (surveyId: number) =>
  queryOptions({
    queryFn: () => getSurveyResponsesFilters(surveyId),
    queryKey: surveyKeys.surveyResponseFilters(surveyId),
    gcTime: 1000 * 60 * 24 * 7,
  })

export type UpdateSurveyResponseTags =
  components["schemas"]["SurveyResponseUpdateTagsDto"][]

export const updateSurveyResponseTags = async (
  tags: UpdateSurveyResponseTags,
) => {
  await client.PUT("/api/Survey/survey-response/tags", {
    body: tags,
  })
}

export const getProjectSurveyResponses = async (
  projectId: number,
  communityId: number,
) => {
  const data = await client.GET("/api/Survey/project-survey-responses", {
    params: {
      query: { projectId, communityId },
    },
  })

  return data
}

export const getProjectSurveyResponsesQuery = (
  projectId?: number,
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: getProjectSurveyResponses,
    queryKeys: surveyKeys.projectSurveyResponses(projectId, communityId),
    unsafeQueryFnArgs: [projectId, communityId],
  })

export const getUserSurveyReponses = async (
  surveyId: number,
  communityId: number,
) => {
  const data = await client.GET("/api/Survey/user-survey-responses", {
    params: {
      query: { surveyId, communityId },
    },
  })

  return data
}

export const getUserSurveyReponsesQuery = (
  surveyId?: number,
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: getUserSurveyReponses,
    queryKeys: surveyKeys.userSurveyResponses(surveyId, communityId),
    unsafeQueryFnArgs: [surveyId, communityId],
  })

export const downloadSurveyResponsesCsv = async (
  surveyId: number,
  filters: SurveyResponsesFilters = {},
) => {
  //using axios directly here because the openapi-axios client expects json responses and tries to parse them
  const response = await client.axiosInstance.post(
    `/api/Survey/ExportSurveyResponses/${surveyId}`,
    filters,
    {
      responseType: "blob",
    },
  )

  return response.data
}

export const getProjectSurveyDefinitions = async (
  projectId: number,
  communityId: number,
) => {
  const data = await client.GET("/api/Survey/project-survey-definitions", {
    params: {
      query: { projectId, communityId },
    },
  })

  return data
}

export const getProjectSurveyDefinitionsQuery = (
  projectId?: number,
  communityId?: number,
) => ({
  ...safeQueryOptions({
    queryFn: getProjectSurveyDefinitions,
    queryKeys: surveyKeys.projectSurveyDefinitions(projectId, communityId),
    unsafeQueryFnArgs: [projectId, communityId],
  }),
  retry: false,
})
